import { ApiService } from "./api.service";
import { IResponse } from "../models/response";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import {
  CreateBuildingModel,
  BuildingDetailsModel,
  BuildingsWithPlantsModel,
} from "../models/buildings/buildings.model";
import { AxiosError } from "axios";
import { MarkerModel } from "../models/map/map.model";
import { ErrorTypeModel } from "../models/error/error.model";

/**
 * Hook to fetch a list of client buildings markers.
 *
 * @param {string} [id] - Optional client ID.
 * @returns {Object} A React Query object with marker data, plus a `refetch` function to manually reload the data.
 */
export const useFetchMarkers = (id?: string) => {
  const queryKey = ["markers", id];

  const { refetch, ...queryResult } = useQuery<
    IResponse<MarkerModel[]>,
    AxiosError
  >(queryKey, () => ApiService.get(`/clients/${id}/buildings-markers`));

  return { ...queryResult, refetch };
};

/**
 * Hook to fetch a list of buildings.
 *
 * @param {string} [id] - Optional client ID.
 * @param {number} [page] - Page number for pagination.
 * @param {number} [itemsPerPage=10] - Number of items per page (default is 10).
 * @param {Record<string, unknown>} [filters] - Filters to apply (optional).
 * @param {Record<string, unknown>} [sort] - Sort options (optional).
 * @returns {Object} A React Query object with buildings list, including loading and error states.
 */
export const useFetchBuildings = (
  id?: string,
  page?: number,
  itemsPerPage: number = 10,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = id
    ? ["buildings", id, page, itemsPerPage, JSON.stringify(filters), sort]
    : ["buildings", page, itemsPerPage, JSON.stringify(filters), sort];

  // Get list of buildings
  return useQuery<IResponse<BuildingDetailsModel[]>, AxiosError>(
    queryKey,
    async () => {
      const url = id ? `/clients/${id}/buildings` : "/buildings";

      const params = {
        page: page,
        per_page: itemsPerPage,
        sortField: sort?.field,
        sortDirection: sort?.sort,
        ...filters,
      };
      try {
        const buildingsData = await ApiService.get(url, { params });
        return buildingsData.data;
      } catch (error) {
        throw error;
      }
    }
  );
};

/**
 * Hook to fetch a list of buildings with their associated plants for a specific client.
 *
 * @param {string | null} id - Client ID to fetch buildings for.
 * @param {Object} [options] - Optional configuration for the query.
 * @param {boolean} [options.enabled] - Whether the query should be enabled (e.g. conditionally fetch after client ID is available).
 * @returns {Object} A React Query object with buildings and plants list, including loading and error states.
 */
export const useFetchBuildingsWithPlants = (
  id: string | null,
  options?: { enabled?: boolean }

) => {
  const queryKey = ["buildings-with-plants", id];

  // Get list of buildings
  return useQuery<IResponse<BuildingsWithPlantsModel[]>, AxiosError>(
    queryKey,
    async () => {
      const url = `/clients/${id}/buildings-with-plants`;

      try {
        const buildingsData = await ApiService.get(url);
        return buildingsData.data;
      } catch (error) {
        throw error;
      }
    },
    options
  );
};

/**
 * Hook to fetch detailed information of a single building.
 *
 * @param {string} [id] - Building ID.
 * @returns {Object} A React Query object with building details, plus a `refetch` function to reload.
 */
export const useGetBuildingDetails = (id?: string) => {
  const queryKey = ["building", id];

  const { refetch, ...queryResult } = useQuery<
    IResponse<BuildingDetailsModel>,
    AxiosError
  >(queryKey, () => ApiService.get(`/buildings/${id}`), {
    staleTime: 60 * 1000,
    enabled: Boolean(id),
  });

  return { ...queryResult, refetch };
};

/**
 * Hook to create a new building.
 *
 * @param {string} clientId - Client ID.
 * @returns {Object} A React Query mutation object for submitting new building data (via a FormData object).
 */
export const useCreateBuilding = (clientId: string) => {
  const createBuilding = useMutation<
    IResponse<CreateBuildingModel>,
    AxiosError<ErrorTypeModel>,
    FormData
  >(async (formData: FormData) => {
    // Make the API request using the formData
    const response = await ApiService.postFormData(
      `/clients/${clientId}/buildings`,
      formData
    );
    return response.data;
  });
  return createBuilding;
};

/**
 * Hook to update an existing building.
 *
 * @param {string} buildingId - Building ID.
 * @returns {Object} A React Query mutation object for updating building data (via a FormData object).
 */
export const useUpdateBuilding = (buildingId: string) => {
  const updateBuilding = useMutation<
    IResponse<CreateBuildingModel>,
    AxiosError<ErrorTypeModel>,
    FormData
  >(async (formData: FormData) => {
    const response = await ApiService.postFormData(
      `/buildings/${buildingId}?_method=PATCH`,
      formData
    );
    return response;
  });
  return updateBuilding;
};

/**
 * Hook to fetch all drinking water buildings with optional search query.
 * Filters buildings where the client has "Kunde von" == "Trinkwasser" (schicht).
 *
 * @param {string} [query] - Optional search query to filter buildings by name, address, or location.
 * @returns {Object} A React Query object with buildings list, including loading and error states.
 */
export const useFetchAllDrinkingWaterBuildings = (
  query: string = "",
  page: number = 1,
  itemsPerPage: number = 12,
  sort?: Record<string, unknown>
) => {
  const queryKey = ["all-drinking-water-buildings", query, page, itemsPerPage, sort];

  return useQuery<IResponse<BuildingDetailsModel[]>, AxiosError>(
    queryKey,
    async () => {
      const params = {
        query: query || "",
        page: page,
        per_page: itemsPerPage,
        sortField: sort?.field,
        sortDirection: sort?.sort,
      };
      try {
        const buildingsData = await ApiService.get(
          "/view-all-drinking-water-buildings",
          { params }
        );
        return buildingsData.data;
      } catch (error) {
        throw error;
      }
    }
  );
};